import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import type { Session } from "@supabase/supabase-js";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { auditLog } from "@/lib/auditLog";
import LocalSelectorModal from "@/components/auth/LocalSelectorModal";

interface AuthCtx {
  session: Session | null;
  tenantId: string | null;
  rol: string | null;
  esAdmin: boolean;
  cargando: boolean;
  salir: () => Promise<void>;
  recargarLocal: () => Promise<void>;
  mostrarSelectorLocal: boolean;
  setMostrarSelectorLocal: (v: boolean) => void;
}

const Ctx = createContext<AuthCtx | null>(null);

// Helper para leer cookies
function getCookie(name: string): string | null {
  if (typeof document === "undefined") return null;
  const match = document.cookie.match(new RegExp(`(^| )${name}=([^;]+)`));
  return match ? decodeURIComponent(match[2]) : null;
}

// Restaurar sesión desde hash fragment (redirect desde login en dominio principal)
async function restaurarSesionDesdeHash(): Promise<boolean> {
  if (typeof window === "undefined") return false;
  const hash = window.location.hash.substring(1); // quitar #
  if (!hash) return false;

  const params = new URLSearchParams(hash);
  const access_token = params.get("access_token");
  const refresh_token = params.get("refresh_token");
  const expires_in = params.get("expires_in");
  const token_type = params.get("token_type");
  const provider_token = params.get("provider_token");
  const provider_refresh_token = params.get("provider_refresh_token");

  if (!access_token || !refresh_token) return false;

  console.log("[AuthProvider] Restaurando sesión desde hash fragment");

  // Construir objeto de sesión compatible con Supabase
  const session: Omit<Session, "user" | "expires_in"> & {
    user: Session["user"] | null;
    expires_in: number;
  } = {
    access_token,
    refresh_token,
    expires_in: expires_in ? parseInt(expires_in, 10) : 3600,
    token_type: (token_type ?? "bearer") as "bearer",
    provider_token: provider_token ?? undefined,
    provider_refresh_token: provider_refresh_token ?? undefined,
    user: null,
  };

  // Guardar en nuestro cookieStorage (que usa Domain=.beautyhub.com.ar)
  const { cookieStorage } = await import("@/lib/supabase");
  const sessionData = JSON.stringify({
    currentSession: session,
    expiresAt: Date.now() + session.expires_in * 1000,
  });
  cookieStorage.setItem("sb-auth-token", sessionData);

  // Limpiar hash de la URL (importante para no repetir)
  window.history.replaceState(
    {},
    document.title,
    window.location.pathname + window.location.search,
  );

  return true;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [session, setSession] = useState<Session | null>(null);
  const [sesionLista, setSesionLista] = useState(false);
  const [tenantId, setTenantId] = useState<string | null>(null);
  const [rol, setRol] = useState<string | null>(null);
  const [tenantLista, setTenantLista] = useState(false);
  const [rolLista, setRolLista] = useState(false);
  const [mostrarSelectorLocal, setMostrarSelectorLocal] = useState(false);
  const [localesDelUsuario, setLocalesDelUsuario] = useState<
    Array<{
      id: string;
      nombre: string;
      slug: string;
      color?: string;
      rol: string;
    }>
  >([]);

  // Refs para acceder a valores actuales en callbacks sin causar re-renders
  const sessionRef = useRef(session);
  const tenantIdRef = useRef(tenantId);
  const sesionListaRef = useRef(sesionLista);

  useEffect(() => {
    sessionRef.current = session;
  }, [session]);
  useEffect(() => {
    tenantIdRef.current = tenantId;
  }, [tenantId]);
  useEffect(() => {
    sesionListaRef.current = sesionLista;
  }, [sesionLista]);

  // Leer tenant de la cookie SÍNCRONAMENTE al montar (evita race condition)
  useLayoutEffect(() => {
    const cookieTenantId = getCookie("tenant_id");
    if (cookieTenantId) {
      setTenantId(cookieTenantId);
      // IMPORTANTE: setear token en PostgREST para que RLS filtre por tenant
      const postgrest = supabase as unknown as {
        postgrest?: { auth?: (token: string) => void };
        rest?: { setAuth?: (token: string) => void };
      };
      void (
        postgrest.postgrest?.auth?.(cookieTenantId) ??
        postgrest.rest?.setAuth?.(cookieTenantId)
      );
    }
    setTenantLista(true);
  }, []);

  // PRIMERO: Intentar restaurar sesión desde hash (redirect desde login)
  useEffect(() => {
    let mounted = true;
    restaurarSesionDesdeHash().then((restaurado) => {
      if (!mounted) return;
      if (restaurado) {
        // Forzar recarga de sesión de Supabase
        supabase.auth.getSession().then(({ data }) => {
          if (data.session) {
            setSession(data.session);
          }
          setSesionLista(true);
        });
      } else {
        setSesionLista(true);
      }
    });
    return () => {
      mounted = false;
    };
  }, []);

  // SEGUNDO: Listener normal de cambios de auth (para refresh, logout, etc.)
  // Se ejecuta UNA sola vez al montar
  useEffect(() => {
    const { data } = supabase.auth.onAuthStateChange(async (_evento, s) => {
      setSession(s);
      if (!sesionListaRef.current) setSesionLista(true);

      // Audit log para login/logout - usar refs para valores actuales
      const currentTenantId = tenantIdRef.current;
      const currentSession = sessionRef.current;

      if (s && !currentSession && currentTenantId) {
        // Login
        await auditLog({ action: "login" }, currentTenantId, s.user.id);
      } else if (!s && currentSession && currentTenantId) {
        // Logout
        await auditLog(
          { action: "logout" },
          currentTenantId,
          currentSession.user.id,
        );
      }
    });
    return () => data.subscription.unsubscribe();
  }, []); // Sin dependencias: una sola suscripción

  // Cargar locales del usuario cuando hay sesión
  useEffect(() => {
    let mounted = true;
    if (session?.user.id && localesDelUsuario.length === 0) {
      supabase
        .from("miembros")
        .select("tenant_id, rol, tenants(id, nombre, slug, config)")
        .eq("user_id", session.user.id)
        .then(({ data, error }) => {
          if (!mounted) return;
          if (error) {
            console.error(
              "[AuthProvider] Error cargando localesDelUsuario:",
              error,
            );
            return;
          }
          if (data) {
            const locales = data.map((m: any) => ({
              id: m.tenant_id,
              nombre: m.tenants?.nombre || "Sin nombre",
              slug: m.tenants?.slug || "",
              color: m.tenants?.config?.color || "#924e7d",
              rol: m.rol,
            }));
            setLocalesDelUsuario(locales);

            // Si tiene más de un local y no hay tenantId en cookie, mostrar selector
            if (locales.length > 1 && !tenantId && !getCookie("tenant_id")) {
              setMostrarSelectorLocal(true);
            }
          }
        });
    }
    return () => {
      mounted = false;
    };
  }, [session, tenantId]);

  // Sincronizar rol desde localesDelUsuario cuando ya estén cargados
  // (cubre el caso: cookie seteó tenantId, luego sesión restauró y cargó locales)
  useEffect(() => {
    if (tenantId && localesDelUsuario.length > 0) {
      const local = localesDelUsuario.find((l) => l.id === tenantId);
      if (local?.rol) {
        setRol(local.rol);
        setRolLista(true);
      }
    }
  }, [tenantId, localesDelUsuario]);

  // Fallback: si no hay tenantId pero hay sesión, consultar miembros del usuario actual
  // También actualiza rol si ya hay tenantId (ej. cookie seteó tenantId pero rol no cargó)
  const recargarLocal = useCallback(async () => {
    const currentSession = sessionRef.current;
    const currentTenantId = tenantIdRef.current;
    const userId = currentSession?.user.id ?? null;

    if (!userId) {
      setTenantId(null);
      setRol(null);
      setTenantLista(true);
      setRolLista(true);
      return;
    }

    // Siempre intentar cargar rol para el tenant actual
    if (currentTenantId) {
      const { data } = await supabase
        .from("miembros")
        .select("rol")
        .eq("user_id", userId)
        .eq("tenant_id", currentTenantId)
        .maybeSingle();
      if (data?.rol) setRol(data.rol);
      setRolLista(true);
    }

    // Fallback: si no hay tenantId, buscar el primero del usuario
    if (!currentTenantId) {
      const { data } = await supabase
        .from("miembros")
        .select("tenant_id, rol")
        .eq("user_id", userId)
        .limit(1)
        .maybeSingle();
      setTenantId(data?.tenant_id ?? null);
      setRol(data?.rol ?? null);
    }
    setTenantLista(true);
    setRolLista(true);
  }, []);

  // Ejecutar recargarLocal cuando cambie la sesión (para buscar tenant al hacer login)
  useEffect(() => {
    recargarLocal();
  }, [session, recargarLocal]);

  // Helper para borrar cookies cross-subdomain
  function borrarCookieTenant() {
    const domain = ".beautyhub.com.ar";
    document.cookie = `tenant_id=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${domain}; SameSite=Lax`;
    document.cookie = `tenant_slug=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${domain}; SameSite=Lax`;
  }

  async function salir() {
    if (tenantId) {
      await auditLog({ action: "logout" }, tenantId, session?.user?.id);
    }
    await supabase.auth.signOut();
    borrarCookieTenant();
    // Limpiar cache de React Query para evitar leak de datos entre locales
    queryClient.clear();
  }

  function handleSeleccionarLocal(tenantId: string, slug: string) {
    setTenantId(tenantId);
    const local = localesDelUsuario.find((l) => l.id === tenantId);
    setRol(local?.rol ?? null);
    setMostrarSelectorLocal(false);

    // Redirigir al subdominio correspondiente
    const hostname = window.location.hostname;
    const expectedHostname = `${slug}.beautyhub.com.ar`;
    if (hostname !== expectedHostname) {
      window.location.href = `https://${expectedHostname}`;
    }
  }

  return (
    <Ctx.Provider
      value={{
        session,
        tenantId,
        rol,
        esAdmin: rol === "owner" || rol === "admin",
        cargando:
          !sesionLista || !tenantLista || !rolLista || mostrarSelectorLocal,
        salir,
        recargarLocal,
        mostrarSelectorLocal,
        setMostrarSelectorLocal,
      }}
    >
      {children}
      {mostrarSelectorLocal && (
        <LocalSelectorModal
          open={mostrarSelectorLocal}
          onOpenChange={setMostrarSelectorLocal}
          onSelect={handleSeleccionarLocal}
        />
      )}
    </Ctx.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useAuth debe usarse dentro de AuthProvider");
  return ctx;
}
